import type { HttpClient } from 'services/api-client/client';
import type { CloudResourceApi } from '@env0/cloud-resource-service/api';
import { returnData } from 'services/api-client/client';

type CloudCompassAssetQuery = { filter: CloudResourceApi.CloudCompassAssetFilter; q: string };
export type CloudCompassAssetCountsQuery = CloudCompassAssetQuery & { groupBy: string };
export type CloudCompassAssetsQuery = CloudCompassAssetQuery & { page: number };

const toAssetQueryParams = ({ filter, q }: CloudCompassAssetQuery) => ({
  ...(Object.keys(filter).length > 0 && { filter: JSON.stringify(filter) }),
  ...(q && { q })
});

type AssetPath = CloudResourceApi.CloudCompassAssetPath;

export type FilterValuesParams = Omit<
  CloudResourceApi.GetCloudCompassFilterValues.Request.QueryParams,
  'organizationId'
>;

const assetUrl = ({ resourceType, platformId }: AssetPath) => `cloud/assets/${resourceType}/${platformId}`;

const getForOrganization = <Response>(client: HttpClient, url: string, organizationId: string, params?: object) =>
  client.get<Response>(url, { params: { organizationId, ...params } }).then(returnData);

export default (client: HttpClient) => ({
  findCloudResources: async (
    body: CloudResourceApi.FindCloudResources.Request.Body
  ): Promise<CloudResourceApi.FindCloudResources.Response> =>
    client.post<CloudResourceApi.FindCloudResources.Response>('/cloud/resources', body).then(returnData),
  updateCloudResources: async (
    body: CloudResourceApi.UpdateCloudResources.Request.Body
  ): Promise<CloudResourceApi.UpdateCloudResources.Response> =>
    client.put<CloudResourceApi.UpdateCloudResources.Response>('/cloud/resources', body).then(returnData),
  startIacGeneration: async (body: CloudResourceApi.StartIacGeneration.Request.Body) =>
    client.post<CloudResourceApi.StartIacGeneration.Response>('/cloud/resources/generate-iac', body).then(returnData),
  getIacGenerationResult: async (jobId: string) =>
    client
      .get<CloudResourceApi.GetIacGenerationResult.Response>(`/cloud/resources/generate-iac/${jobId}`)
      .then(returnData),
  getChangeEventUrl: async (eventId: string) =>
    client.get<CloudResourceApi.GetChangeEventUrl.Response>(`cloud/resources/events/${eventId}`).then(returnData),
  getResourceChangeEvents: async (cloudResourceId: string) =>
    client
      .get<CloudResourceApi.GetResourceEvents.Response>(`cloud/resources/${cloudResourceId}/events`)
      .then(returnData),
  getResourceFullEvent: async (eventId: string) =>
    client.get<CloudResourceApi.GetEventById.Response>(`cloud/events/${eventId}`).then(returnData),
  getCloudCompassFilterValues: async (organizationId: string, dimension: string, params?: FilterValuesParams) =>
    getForOrganization<CloudResourceApi.GetCloudCompassFilterValues.Response>(
      client,
      `cloud/filters/${dimension}/values`,
      organizationId,
      params
    ),
  getCloudCompassAssetCounts: async (organizationId: string, query: CloudCompassAssetCountsQuery) =>
    client
      .get<CloudResourceApi.GetCloudCompassAssetCounts.Response>('cloud/assets/counts', {
        params: { organizationId, groupBy: query.groupBy, ...toAssetQueryParams(query) }
      })
      .then(returnData),
  getCloudCompassTree: async (organizationId: string) =>
    getForOrganization<CloudResourceApi.GetCloudCompassTree.Response>(client, 'cloud/tree', organizationId),
  getCloudCompassUnmanagedAttribution: async (organizationId: string) =>
    getForOrganization<CloudResourceApi.GetCloudCompassUnmanagedCounts.Response>(
      client,
      'cloud/unmanaged-attribution',
      organizationId
    ),
  getCloudCompassInsights: async (organizationId: string, scope: CloudResourceApi.GetCloudCompassInsights.Scope) =>
    getForOrganization<CloudResourceApi.GetCloudCompassInsights.Response>(client, 'cloud/insights', organizationId, {
      scope
    }),
  getCloudCompassManagedResources: async (organizationId: string, environmentId: string) =>
    getForOrganization<CloudResourceApi.GetCloudCompassManagedResources.Response>(
      client,
      `cloud/environments/${environmentId}/managed-resources`,
      organizationId
    ),
  getCloudCompassUnmanagedResources: async (organizationId: string, environmentId: string) =>
    getForOrganization<CloudResourceApi.GetCloudCompassUnmanagedResources.Response>(
      client,
      `cloud/environments/${environmentId}/unmanaged-resources`,
      organizationId
    ),
  getCloudCompassUnmanagedEnvironments: async (organizationId: string) =>
    getForOrganization<CloudResourceApi.GetCompassUnmanagedEnvironments.Response>(
      client,
      'cloud/unmanaged-environments',
      organizationId
    ),
  getCloudCompassUnmanagedEnvironmentResources: async (organizationId: string, unmanagedEnvironmentId: string) =>
    getForOrganization<CloudResourceApi.GetCloudCompassUnmanagedEnvAssets.Response>(
      client,
      `cloud/unmanaged-environments/${unmanagedEnvironmentId}/resources`,
      organizationId
    ),
  getCloudCompassUnmanagedRemainder: async (organizationId: string, page: number) =>
    getForOrganization<CloudResourceApi.GetCloudCompassUnmanagedRemainder.Response>(
      client,
      'cloud/unmanaged/resources',
      organizationId,
      { page }
    ),
  getCloudCompassUnmanagedRemainderTotal: async (organizationId: string) =>
    getForOrganization<CloudResourceApi.GetCloudCompassUnmanagedRemainder.Response>(
      client,
      'cloud/unmanaged/resources',
      organizationId,
      { totalOnly: 'true' }
    ),
  getCloudCompassAssetDetails: async (organizationId: string, path: AssetPath) =>
    getForOrganization<CloudResourceApi.GetCloudCompassAssetDetails.Response>(
      client,
      `${assetUrl(path)}/details`,
      organizationId
    ),
  getCloudCompassNativeRow: async (organizationId: string, path: AssetPath, relatedTo?: AssetPath) =>
    getForOrganization<CloudResourceApi.GetCloudCompassNativeRow.Response>(
      client,
      assetUrl(path),
      organizationId,
      relatedTo && { relatedToResourceType: relatedTo.resourceType, relatedToPlatformId: relatedTo.platformId }
    ),
  getCloudCompassAssetRelated: async (organizationId: string, path: AssetPath) =>
    getForOrganization<CloudResourceApi.GetCloudCompassAssetRelated.Response>(
      client,
      `${assetUrl(path)}/related`,
      organizationId
    ),
  checkCloudCompassManagedStatus: async (organizationId: string, resources: AssetPath[]) =>
    client
      .post<CloudResourceApi.CheckCloudCompassManagedStatus.Response>(
        'cloud/assets/managed-status',
        { resources },
        { params: { organizationId } }
      )
      .then(returnData),
  getCloudCompassAssets: async (organizationId: string, query: CloudCompassAssetsQuery) =>
    client
      .get<CloudResourceApi.GetCloudCompassAssets.Response>('cloud/assets', {
        params: { organizationId, page: query.page, ...toAssetQueryParams(query) }
      })
      .then(returnData)
});
